import { TrendingUp } from 'lucide-react';

interface ConversionFunnelProps {
  pageviews: number;
  started: number;
  completed: number;
}

const ConversionFunnel = ({ pageviews, started, completed }: ConversionFunnelProps) => {


  const bounced = Math.max(0, pageviews - started);
  const bouncePct = pageviews > 0 ? Math.round((bounced / pageviews) * 100) : 0;
  const startPct = pageviews > 0 ? Math.round((started / pageviews) * 100) : 0;
  const completePct = pageviews > 0 ? Math.round((completed / pageviews) * 100) : 0;
  const droppedOff = started - completed;
  const dropOffPct = started > 0 ? Math.round((droppedOff / started) * 100) : 0;

  // Market benchmarks for skincare quiz funnels (16+ questions)
  const BENCHMARKS = {
    bounceRate: { min: 40, max: 60, label: 'Bounce rate' },
    startRate: { min: 40, max: 60, label: 'Start rate' },
    cvr: { min: 25, max: 45, label: 'CVR' },
  };

  const getBenchmarkStatus = (value: number, benchmark: { min: number; max: number }) => {
    if (value >= benchmark.min && value <= benchmark.max) return 'normal';
    if (value > benchmark.max) return value > benchmark.max + 10 ? 'great' : 'normal';
    return 'low';
  };

  const funnelSteps = [
    { label: 'Pageviews', value: pageviews, pct: 100, color: '#0F1C2E' },
    { label: 'Gestart (scherm 2+)', value: started, pct: pageviews > 0 ? Math.round((started / pageviews) * 100) : 0, color: '#2563eb' },
    { label: 'Voltooid', value: completed, pct: pageviews > 0 ? Math.round((completed / pageviews) * 100) : 0, color: '#10b981' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2">
        <TrendingUp className="w-4 h-4" /> Conversie-funnel
        <span className="ml-auto flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          LIVE
        </span>
      </h3>
      <div className="space-y-6">
        <div className="space-y-3">
          {funnelSteps.map((step, i) => (
            <div key={step.label}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{step.label}</span>
                <span className="text-sm text-gray-500">{step.value} ({step.pct}%)</span>
              </div>
              <div className="h-8 bg-gray-100 rounded-lg overflow-hidden">
                <div
                  className="h-full rounded-lg transition-all duration-500"
                  style={{ width: `${Math.min(step.pct, 100)}%`, backgroundColor: step.color }}
                />
              </div>
              {i === 0 && bounced > 0 && (
                <p className="text-xs text-red-500 mt-1 ml-1">↳ {bounced} bezoekers bounced ({bouncePct}%)</p>
              )}
              {i === 1 && droppedOff > 0 && (
                <p className="text-xs text-orange-500 mt-1 ml-1">↳ {droppedOff} afgehaakt ({dropOffPct}% van gestart)</p>
              )}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-red-500">{`${bouncePct}%`}</p>
            <p className="text-xs text-gray-500">Bounce rate</p>
            <p className={`text-[10px] mt-0.5 font-medium ${getBenchmarkStatus(bouncePct, BENCHMARKS.bounceRate) === 'normal' ? 'text-gray-400' : bouncePct < BENCHMARKS.bounceRate.min ? 'text-emerald-500' : 'text-red-400'}`}>
              Benchmark: {BENCHMARKS.bounceRate.min}-{BENCHMARKS.bounceRate.max}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{startPct}%</p>
            <p className="text-xs text-gray-500">Start rate</p>
            <p className={`text-[10px] mt-0.5 font-medium ${getBenchmarkStatus(startPct, BENCHMARKS.startRate) === 'normal' ? 'text-gray-400' : startPct > BENCHMARKS.startRate.max ? 'text-emerald-500' : 'text-orange-400'}`}>
              Benchmark: {BENCHMARKS.startRate.min}-{BENCHMARKS.startRate.max}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-500">{completePct}%</p>
            <p className="text-xs text-gray-500">Conversie (CVR)</p>
            <p className={`text-[10px] mt-0.5 font-medium ${getBenchmarkStatus(completePct, BENCHMARKS.cvr) === 'normal' ? 'text-gray-400' : completePct > BENCHMARKS.cvr.max ? 'text-emerald-500' : 'text-orange-400'}`}>
              Benchmark: {BENCHMARKS.cvr.min}-{BENCHMARKS.cvr.max}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionFunnel;
